import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as MonitoringActions from "../../../actions/monitoring";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import { NotificationAlert } from "../../../components/common/index";
import { DatePickerFormInputFabric, TextAreaFormInput } from "../../../components/form";
import { CheckboxFormInput, SelectFormInput, TextFormInput } from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionMonitoringCreateBreadcrumbs, TrialInstitutionMonitoringDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { MonitoringSubMenu } from "../../../helpers/subMenuHelper";
import { useNavigate } from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { createPersonnelSearchLoader } from "../../../functions/personnel";

import "./MonitoringPage.scss";

interface MonitoringPageProps {
    form: Dtos.Monitoring & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    monitoring: Dtos.Monitoring;
    lookups: Dtos.Lookup[];
    loadingMonitoring: boolean;
    loadMonitoringSuccess: boolean;
    loadMonitoringFailure: boolean;
    loadMonitoringErrorCode: string;
    savingMonitoring: boolean;
    saveMonitoringSuccess: boolean;
    saveMonitoringFailure: boolean;
    creatingMonitoring: boolean;
    createMonitoringSuccess: boolean;
    createMonitoringFailure: boolean;
    validatingMonitoring: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    pageSize: number;
    clearMonitoring: MonitoringActions.IMonitoringClearActionCreator;
    loadMonitoring: MonitoringActions.IMonitoringLoadByIdActionCreator;
    saveMonitoring: MonitoringActions.IMonitoringSaveActionCreator;
    createMonitoring: MonitoringActions.IMonitoringCreateActionCreator;
    validateMonitoring: MonitoringActions.IMonitoringValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const reduxFormName: string = "reduxForms.monitoring";

const MonitoringPage: FC<MonitoringPageProps> = ({
    form,
    formState,
    formProperties,
    monitoring,
    lookups,
    loadingMonitoring,
    //loadMonitoringSuccess,
    loadMonitoringFailure,
    loadMonitoringErrorCode,
    savingMonitoring,
    saveMonitoringSuccess,
    saveMonitoringFailure,
    creatingMonitoring,
    //createMonitoringSuccess,
    //createMonitoringFailure,
    validatingMonitoring,
    validationFailures,
    permissions,
    trialInstitution,
    loadingTrialInstitution,
    //loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    clearMonitoring,
    loadMonitoring,
    saveMonitoring,
    createMonitoring,
    validateMonitoring,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialInstitution,
    loadTrialInstitution,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const monitoringIdNumber = parseInt(monitoringId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.Monitoring = {} as Dtos.Monitoring;

        loadForm(
            reduxFormName,
            Object.assign({ ...monitoring }, extraData)
        );
    };

    // Load/Create monitoring
    useEffect(() => {
        if (monitoringIdNumber && trialInstitutionIdNumber && trialIdNumber && !monitoring && !loadingMonitoring) {
            setBreadcrumbs(TrialInstitutionMonitoringDetailsBreadcrumbs(trialIdNumber, trialInstitutionIdNumber, monitoringIdNumber));

            loadMonitoring(monitoringIdNumber);
        }
        else if (!monitoringIdNumber && trialInstitutionIdNumber && trialIdNumber && !monitoring && !creatingMonitoring) {
            setDrawerItems([]);
            setBreadcrumbs(TrialInstitutionMonitoringCreateBreadcrumbs(trialIdNumber, trialInstitutionIdNumber));

            createMonitoring(trialInstitutionIdNumber);
        }
        else if (monitoring && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        monitoringIdNumber,
        trialInstitutionIdNumber,
        trialIdNumber,
        monitoring,
        loadingMonitoring,
        creatingMonitoring,
        formInitalised,
        trialInstitution,
        loadingTrialInstitution
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringFailure) {
            navigate(`/error/${loadMonitoringErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
    }, [
        loadMonitoringFailure,
        loadMonitoringErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Update breadcrumbs & drawer items
    useEffect(() => {
        if (monitoringIdNumber && trialIdNumber && trialInstitutionIdNumber && trialInstitution && permissions) {
            setDrawerItems(MonitoringSubMenu(trialIdNumber, trialInstitutionIdNumber, monitoringIdNumber, permissions));
            setBreadcrumbs(
                TrialInstitutionMonitoringDetailsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    monitoringIdNumber,
                    trialInstitution?.trial?.displayName,
                    trialInstitution?.institution?.name
                )
            );
        }
        else if (!monitoringIdNumber && trialInstitutionIdNumber && trialIdNumber && trialInstitution) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        monitoringIdNumber,
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        monitoring,
        permissions
    ]);

    // Reload monitoring after saving
    useEffect(() => {
        if (!savingMonitoring && saveMonitoringSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Monitoring saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring`);
            } else if (!monitoringIdNumber) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoring.id}/details`);
                clearMonitoring();
                clearTrialInstitution();
            }
        }
    }, [
        savingMonitoring,
        saveMonitoringSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingMonitoring && saveMonitoringFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingMonitoring, saveMonitoringFailure]);

     // Cleanup effect to clear the monitoring & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoring();
            clearTrialInstitution();
            resetForm(reduxFormName);
        };
    }, [clearMonitoring, clearTrialInstitution, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Monitoring = {} as Dtos.Monitoring;

            if (!data.trialInstitutionId) {
                changes.trialInstitutionId = trialInstitutionIdNumber;
            }

            if (data.monitors?.length > 0) {
                changes.monitors = 
                    data.monitors.map((x) => 
                        ({ 
                            monitoringId: monitoringIdNumber, 
                            personnelId: x.personnelId ?? x.value, 
                            active: true 
                        })
                    );
            }

            await saveMonitoring(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.Monitoring) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring`);
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Monitoring = {} as Dtos.Monitoring;

            if (!form.trialInstitutionId) {
                changes.trialInstitutionId = trialInstitutionIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingMonitoring) {
                await validateMonitoring(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.monitors?.length > 0 
                ? form.monitors?.map((x) => 
                    ({ 
                        label: x?.personnel?.displayWithSalutation, 
                        value: x?.personnelId 
                    }) as Dtos.PersonnelOption
                ) 
                : null
        );
    }, [form]);

    const defaultMonitorOptions = useMemo(() => {
        return form.monitors?.map((x) => 
            ({ 
                label: x?.personnel?.displayWithSalutation, 
                value: x?.personnelId 
            }) as Dtos.PersonnelOption
        );
    }, [form]);

    const selectedMonitors = useMemo(() => {
        return form.monitors?.map((x: any) => 
            ({ 
                label: x?.personnel?.displayWithSalutation ?? x?.label, 
                value: x?.personnelId ?? x?.value 
            } as Dtos.PersonnelOption
        )) ?? [];
    }, [form.monitors]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.MonitoringUpdate,
                Dtos.Permission.MonitoringCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingMonitoring ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        const approvedMonitoringStatusId = findLookup("Monitoring", "monitoringStatusId", lookups)?.values?.find(x => x.value == "Approved")?.id;

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            visitGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            startDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            endDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            monitors: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    multi: true,
                                                    value: selectedMonitors,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.monitors?.length > 0
                                                        ? defaultMonitorOptions
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-md-2",
                                                fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            monitoringVisitTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "monitoring",
                                                                "monitoringVisitTypeId",
                                                                lookups
                                                            ),
                                                            form?.monitoringVisitTypeId
                                                        ),
                                                    clearable: true
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            monitoringMeetingId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "monitoring",
                                                                "monitoringMeetingId",
                                                                lookups
                                                            ),
                                                            form?.monitoringMeetingId
                                                        ),
                                                    clearable: true
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            monitoringPharmacyVisitId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "monitoring",
                                                                "monitoringPharmacyVisitId",
                                                                lookups
                                                            ),
                                                            form?.monitoringPharmacyVisitId
                                                        ),
                                                    clearable: true
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            pharmacyClosureDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: true,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            hoursMonitoring: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            calculatedMonitoringFee: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: true,
                                                inputProps: {
                                                    value: !form?.hoursMonitoring ? `` : `$${form.hoursMonitoring * 70}.00`,
                                                },
                                                inputLabel: "Calculated Monitoring Fee",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            reportGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Report Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            submittedTo: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            submittedDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            monitoringStatusId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "monitoring",
                                                                "monitoringStatusId",
                                                                lookups
                                                            ),
                                                            form?.monitoringStatusId
                                                        ),
                                                    clearable: true
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                                changeAction: (_model, value, _form, _parentModel) => {
                                                    changeForm(`${reduxFormName}.monitoringStatusId`, value);
                                                    if (value != approvedMonitoringStatusId) {
                                                        changeForm(`${reduxFormName}.approvedDate`, null);
                                                    }
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            approvedDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled || form?.monitoringStatusId != approvedMonitoringStatusId,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-8 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            uploadedDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled || form?.uploadedNa,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            uploadedNa: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Not Applicable",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName: "col-form-label col-sm-0 d-none",
                                                fieldClassName: "col-sm-12 generic-form-field",
                                                changeAction: (_model, value, _form, _parentModel) => {
                                                    changeForm(`${reduxFormName}.uploadedNa`, value);
                                                    if (value) {
                                                        changeForm(`${reduxFormName}.uploadedDate`, null);
                                                    }
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            mailedDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled || form?.mailedNa,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-4 col-xl-12",
                                                fieldClassName: "col-xxl-7 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            mailedNa: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Not Applicable",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName: "col-form-label col-sm-0 d-none",
                                                fieldClassName: "col-sm-12 generic-form-field",
                                                changeAction: (_model, value, _form, _parentModel) => {
                                                    changeForm(`${reduxFormName}.mailedNa`, value);
                                                    if (value) {
                                                        changeForm(`${reduxFormName}.mailedDate`, null);
                                                    }
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )
            },
            summaryGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Monitoring Summary</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            minorDeviations: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-2 col-xl-12",
                                                fieldClassName: "col-xxl-10 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "monitoring",
                                        {
                                            comments: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-xxl-2 col-xl-12",
                                                fieldClassName: "col-xxl-10 col-xl-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
        };

        return (
            <>
                <div className="row">
                    <div className={"col-auto"}>
                        <h1>Monitoring Visit</h1>
                    </div>
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "monitoring",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        findLookup,
        lookups,
        generateOptionsFromLookup,
        form,
        permissions,
        savingMonitoring,
        formState,
        trialInstitutionIdNumber,
        renderFormControls,
        validationFailures,
        formProperties,
        changeForm,
        trialInstitution,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        onCancel,
        defaultMonitorOptions,
        selectedMonitors
    ]);

    return (
        <RestrictedLayout
            loading={
                loadingMonitoring || 
                creatingMonitoring ||
                loadingTrialInstitution
            }
            permission={Dtos.Permission.MonitoringView}
        >
            { renderContent }
        </RestrictedLayout>
    )
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringPageProps> => {
    let monitoring: Dtos.Monitoring | undefined = !(state.monitoring.formData instanceof Array)
        ? state.monitoring.formData
        : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined = !(state.trialInstitution.formData instanceof Array)
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        monitoring: monitoring!,
        loadingMonitoring: state.monitoring.loadState && state.monitoring.loadState.status == RequestState.Pending,
        loadMonitoringSuccess: state.monitoring.loadState && state.monitoring.loadState.status == RequestState.Success,
        loadMonitoringFailure: state.monitoring.loadState && state.monitoring.loadState.status == RequestState.Failure,
        loadMonitoringErrorCode: state.monitoring.loadState.errorCode,
        savingMonitoring: state.monitoring.saveState && state.monitoring.saveState.status == RequestState.Pending,
        saveMonitoringSuccess: state.monitoring.saveState && state.monitoring.saveState.status == RequestState.Success,
        saveMonitoringFailure: state.monitoring.saveState && state.monitoring.saveState.status == RequestState.Failure,
        creatingMonitoring: state.monitoring.createState && state.monitoring.createState.status == RequestState.Pending,
        createMonitoringSuccess: state.monitoring.createState && state.monitoring.createState.status == RequestState.Success,
        createMonitoringFailure: state.monitoring.createState && state.monitoring.createState.status == RequestState.Failure,
        validatingMonitoring: state.monitoring.validateState && state.monitoring.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.monitoring.lookups,
        form: state.reduxForms.monitoring,
        formState: state.monitoring.formState,
        formProperties: state.monitoring.formProperties,
        validationFailures: monitoring
            ? state.monitoring.validationFailures
            : undefined,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadMonitoring: bindActionCreators(MonitoringActions.LoadMonitoringById, dispatch),
        clearMonitoring: bindActionCreators(MonitoringActions.Clear, dispatch),
        saveMonitoring: bindActionCreators(MonitoringActions.SaveMonitoring, dispatch),
        createMonitoring: bindActionCreators(MonitoringActions.CreateMonitoring, dispatch),
        validateMonitoring: bindActionCreators(MonitoringActions.ValidateMonitoring, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringPage);